<!--
  Dispatch's own table -- one instance per state (in-progress / delivered),
  owning its own fetch, sort, row actions and the three edit dialogs. Migrated
  off `cTablePaginated` / `cTooltip` / `cDialog` onto the `lw` library
  (refresh_plan.md section 4, surface 1: "the page a dispatcher lives on").

  The h2 below is the level under LwPageHeader's own <h1> (Dispatch.vue), not
  a page title of its own; LwTable's `caption` carries the same text as the
  table's accessible name, visually hidden, because the h2 is already on
  screen (audit_web.md 4.4: no page in the app has a real heading hierarchy
  today).
-->
<template>
  <section class="lw-p-board" data-tour="dispatch.board">
    <h2 class="lw-p-board__title">
      {{ title }}
    </h2>

    <!--
      The find box. Server-side (lib/loadSearch.js's header says why it has to
      be on this surface): the board holds ONE page of 25, so a local filter
      could only ever search the page a dispatcher is already looking at.

      A real labelled field, not the unlabelled magnifier input the old cTable
      toolbar used, matching the pattern CustomersList already establishes.
      The placeholder names what can be typed because "Search" alone leaves a
      dispatcher guessing which of the eight columns it means.
    -->
    <div class="lw-p-board__toolbar">
      <LwField
        class="lw-p-board__search"
        type="search"
        :label="`Search ${title}`"
        placeholder="Order number, customer, driver, truck, trailer or stop"
        :model-value="searchQuery"
        @update:model-value="handleSearchInput"
      />
    </div>

    <!--
      DENSITY, and the arithmetic behind it, because "denser" is a claim and
      the board is the one page where it has to be checked rather than
      asserted. Paper cut 15: Dispatch rows are ~49px tall for one line of
      content today. `--lw-density-row-compact-height` is 36px and
      `--lw-density-row-comfortable-height` is 52px, so comfortable would have
      made this page WORSE than the thing it replaces; compact is not a
      stylistic preference here, it is the only one of the two that is allowed.

      At 1440x900 this page spends roughly 224px above its first row (48px app
      header + 16px content gutter + ~70px LwPageHeader + 24px body gutter +
      ~36px section heading and gap + ~30px table header) against roughly 209px
      before the migration -- a real page header costs about 15px more than the
      content-header it replaces. That leaves 676px of rows now against 691px
      before: 18 rows at 36px against 14 at 49px. The page size matters more
      than the row height did: cTablePaginated hardcoded `:per="10"` for every
      table that used it (paper cut 11), so a screen that could show 18 rows
      was being handed 10. LoadList.test.js turns this paragraph into an
      assertion rather than leaving it as a comment nobody re-checks.
    -->
    <!--
      F3 (ai_docs/perf/web_memory_investigation.md): the board is
      server-paged. `loads` is one page of 25, `total`/`page` put LwTable's
      pagination in controlled mode, and both the sort and page-change
      events refetch rather than rearranging rows the client no longer
      holds.
    -->
    <LwTable
      :caption="title"
      :columns="columns"
      :rows="loads"
      :loading="loading"
      :error="error"
      :empty-title="displayEmptyTitle"
      :empty-body="displayEmptyBody"
      :sort="sort"
      density="compact"
      :page-size="25"
      :page="page"
      :total-rows="total"
      row-activate-label="View load"
      @sort="handleSort"
      @retry="loadData"
      @row-activate="handleRowActivate"
      @page-change="handlePageChange"
    >
      <template #cell-created_at="{ row }">
        {{ formatDate(row) }}
      </template>

      <template #cell-order_number="{ row }">
        {{ orderNumber(row) }}
      </template>

      <template #cell-customer="{ row }">
        <!--
          Replaces the c-tooltip hover card that used to carry the address
          and phone (audit_web.md 3.4): that widget opened on @mouseenter
          only, with no keyboard or assistive-tech path at all, and the
          library builds no tooltip primitive to migrate it onto -- inventing
          one is outside this surface. A native `title` attribute is not a
          full replacement (it is also mouse-hover-only in most browsers),
          but it is honest about that rather than pretending otherwise, and
          the load detail page (surface 2.2) is where the contact block
          belongs as real, keyboard-reachable content.
        -->
        <span :title="customerTitle(row)">{{ customerName(row) }}</span>
      </template>

      <template #cell-route="{ row }">
        <!--
          SEC-N (escapeHtml.js): stop names are org-entered free text and
          this cell is a v-html sink -- unchanged from before the migration.
          Escape first, then add the one piece of intentional markup (the
          arrow). Do not touch this without re-reading escapeHtml.js's header
          comment.
        -->
        <span v-html="formatRoute(row.stops)" />
      </template>

      <template #cell-custody="{ row }">
        <!-- data-tour: the guided tour points at ONE load's custody and tracking
             cells by its PO (ui/src/app/tours; a test holds these names in place). -->
        <span data-tour="dispatch.custody" :data-tour-key="row.po">
          <LwChip axis="custody" :value="custodyFor(row)" />
        </span>
      </template>

      <template #cell-telemetry="{ row }">
        <!--
          telemetryFor(row) is a pure, cheap lookup (boardState.js has no
          network or clock of its own), so calling it more than once per row
          here is a readability trade, not a performance one: Vue 2 templates
          have no local "let", and a v-for-of-one-item stand-in for that would
          trip vue/valid-v-for over the loop variable the key does not use.
        -->
        <span
          v-if="telemetryFor(row)"
          class="lw-p-board__telemetry"
          data-tour="dispatch.tracking"
          :data-tour-key="row.po"
        >
          <LwChip
            axis="telemetry"
            :value="telemetryFor(row).value"
            :label="telemetryLabel(telemetryFor(row))"
          />
          <span v-if="telemetryFor(row).ageLabel" class="lw-p-board__telemetry-age">{{
            telemetryFor(row).ageLabel
          }}</span>
        </span>
        <!--
          null means this row's custody axis suppresses telemetry entirely
          (boardState.js's TELEMETRY_SUPPRESSED_CUSTODY: delivered, awaiting
          pickup, cancelled -- tracker freshness is not a question anyone can
          act on for those rows). A bare em dash would read to a screen
          reader as punctuation with no meaning, so the dash is aria-hidden
          and a real word carries the fact.
        -->
        <span v-else class="lw-p-board__telemetry-empty">
          <span aria-hidden="true">&mdash;</span>
          <span class="lw-p-board__sr-only">Not applicable</span>
        </span>
      </template>

      <!--
        Spec 30 phase A: "Driver or subhauler". A load handed to a subhauler
        shows the carrier's name, with the person actually driving beneath it
        when the dispatcher has typed one; the API keeps the two assignments
        exclusive, so at most one branch is ever true.
      -->
      <template #cell-driver="{ row }">
        <template v-if="row.subhauler">
          <span class="lw-p-board__subhauler">{{ row.subhauler.name }}</span>
          <span v-if="row.subhauler_driver_name" class="lw-p-board__subhauler-driver">{{
            row.subhauler_driver_name
          }}</span>
        </template>
        <template v-else>
          <span data-tour="dispatch.driver" :data-tour-key="row.po">{{
            row.driver ? row.driver.name : 'No driver set'
          }}</span>
        </template>
        <!--
          Spec 39: where the driver's assignment offer stands (acceptance on).
          The board read only sends an offer about the driver on the load
          now, or a decline on a load left with no driver.
        -->
        <span v-if="offerChipFor(row)" class="lw-p-board__offer" data-test="board-offer">
          <LwChip
            axis="feedback"
            :value="offerChipFor(row).value"
            :label="offerChipFor(row).label"
            :title="offerChipFor(row).title"
          />
        </span>
      </template>

      <template #cell-equipment="{ row }">
        <span data-tour="dispatch.equipment" :data-tour-key="row.po">{{ equipmentFor(row) }}</span>
      </template>

      <template #cell-actions="{ row }">
        <LwMenu
          :label="`Actions for load ${orderNumber(row)}`"
          :items="actionsFor(row)"
          data-tour="dispatch.row-actions"
          :data-tour-key="row.po"
        />
      </template>
    </LwTable>

    <!--
      Spec 30 phase A: drivers and subhaulers in one picker. Since 2026-10-10
      (Mark: "instead of the dropdown, let's make this a datatable select")
      it is a table with every driver's availability for this load already
      shown and a Select button per row that assigns at once; a pick that
      needs an override opens a reason step first (AssigneePicker.vue). The
      PATCH, the 409 and the refresh stay here.
    -->
    <LwDialog
      :open="driverModalLoad !== null"
      title="Set driver or subhauler"
      size="wide"
      data-tour="dispatch.driver-dialog"
      initial-focus="input[type=search]"
      @cancel="closeDriverModal"
    >
      <AssigneePicker
        v-if="driverModalLoad"
        :load="driverModalLoad"
        :drivers="drivers"
        :subhaulers="subhaulers"
        :zone="schedulingZone"
        :can-override="canManageScheduling"
        :busy="driverModalSaving"
        :problem="assignmentProblem"
        @assign="assignFromPicker"
      />
      <template #actions="{ cancel }">
        <LwButton variant="secondary" @click="cancel">Close</LwButton>
      </template>
    </LwDialog>

    <!--
      The old template filtered `trucks` down to type="truck" / type="trailer"
      with `v-for` + `v-if` on the SAME element, a combination Vue explicitly
      warns against (the v-if re-evaluates per iteration against a v-for scope
      that has not fully resolved). The filtering now happens once, in a
      computed, for each select.
    -->
    <LwDialog
      :open="truckModalLoad !== null"
      title="Set truck/trailer"
      size="wide"
      confirm-label="Save"
      data-tour="dispatch.truck-dialog"
      @confirm="confirmSetTruckTrailer"
      @cancel="closeTruckModal"
    >
      <LwField
        v-model="truckModalTruckId"
        type="select"
        label="Truck"
        data-tour="dispatch.truck-select"
        placeholder="Choose a truck"
        :options="truckOptions"
      />
      <!-- Spec 46: the trailer is a table with each trailer's state for this
           load (EquipmentPicker.vue); a pick that needed a reason fills the
           reason box below, so Save sends it as scheduling_override. -->
      <EquipmentPicker
        v-if="truckModalLoad"
        kind="trailer"
        :load="truckModalLoad"
        :can-override="canManageScheduling"
        :selected-id="truckModalTrailerId"
        @pick="pickTrailer"
      />
      <!-- D-08: the same decision for the truck (held, inactive, double booked),
           and since spec 37 for the trailer too (expired credentials). -->
      <AvailabilityNotice
        v-if="truckModalTruckId || truckModalTrailerId"
        :decision="truckAvailability"
        :loading="truckAvailabilityLoading"
        :zone="schedulingZone"
        :can-override="canManageScheduling"
      />
      <LwNotice v-if="assignmentProblem" tone="danger" :title="assignmentProblem.title">
        <ul v-if="assignmentProblem.reasons.length" class="lw-p-load-list__refusal">
          <li v-for="(reason, index) in assignmentProblem.reasons" :key="index">{{ reason }}</li>
        </ul>
      </LwNotice>
      <LwField
        v-if="(truckModalTruckId || truckModalTrailerId) && overrideOffered(truckAvailability)"
        v-model="overrideReason"
        type="textarea"
        :rows="2"
        label="Reason to assign anyway"
        description="Required to assign over a warning; recorded on the load."
      />
    </LwDialog>

    <!--
      Spec 44: "Add to a run…". The open runs (GET /runs, undelivered), each
      "driver · truck · N loads"; picking one is POST /runs/:id/loads, an
      assignment through the same availability decision as the dialogs above,
      so a 409 is shown the same way and a scheduling.manage viewer may type
      a reason and pick again. The run builder is where the order is set.
    -->
    <LwDialog
      :open="runPickerLoad !== null"
      title="Add to a run"
      data-test="run-picker"
      @cancel="closeRunPicker"
    >
      <p v-if="runsLoading" class="lw-p-load-list__runs-status">Loading open runs…</p>
      <LwNotice v-else-if="runsFailed" tone="danger">The runs could not be loaded.</LwNotice>
      <p
        v-else-if="runAddedId !== null"
        class="lw-p-load-list__runs-status"
        role="status"
        data-test="run-picker-added"
      >
        Added to run {{ runAddedId }}.
        <LwButton variant="link" size="sm" :href="runHref(runAddedId)" @click="openRun($event)">
          Open the run
        </LwButton>
      </p>
      <p v-else-if="!openRuns.length" class="lw-p-load-list__runs-status">
        No open runs. Start one on the Runs page.
      </p>
      <ul v-else class="lw-p-load-list__runs">
        <li v-for="run in openRuns" :key="run.id">
          <LwButton
            variant="secondary"
            size="sm"
            :loading="runPickBusy === run.id"
            :disabled="runPickBusy !== null && runPickBusy !== run.id"
            :data-test="`run-option-${run.id}`"
            @click="addToRun(run)"
          >
            {{ runOptionLabel(run) }}
          </LwButton>
        </li>
      </ul>
      <template v-if="runPickProblem && runAddedId === null">
        <AvailabilityNotice
          v-if="runPickProblem.decision"
          :decision="runPickProblem.decision"
          :zone="schedulingZone"
          :can-override="canManageScheduling"
        />
        <LwNotice v-else tone="danger" :title="runPickProblem.title" />
        <LwField
          v-if="overrideOffered(runPickProblem.decision)"
          v-model="runPickReason"
          type="textarea"
          :rows="2"
          label="Reason to add anyway"
          description="Type a reason, then pick the run again; it is recorded on the load."
        />
      </template>
      <template #actions="{ cancel }">
        <LwButton variant="secondary" @click="cancel">Close</LwButton>
      </template>
    </LwDialog>

    <LwDialog
      :open="cancelModalLoad !== null"
      variant="destructive"
      title="Cancel load"
      confirm-label="Cancel load"
      description="This cannot be undone."
      @confirm="confirmCancelLoad"
      @cancel="closeCancelModal"
    >
      <p>Are you sure you want to delete this load?</p>
    </LwDialog>
  </section>
</template>

<script>
import moment from 'moment';
import { orgZone } from '../../lib/orgTime.js';
import { printerMixin, renderSheet } from '../../lib/printer';
import loadsPrint from '../../components/pages/Loads/loadsPrint.vue';
import { escapeHtml } from '../../lib/escapeHtml';
import { custodyForLoad, telemetryForLoad } from '../../lib/boardState';
import { loadSearchMixin } from '../../lib/loadSearch';
import LwTable from '../../components/lw/LwTable.vue';
import LwChip from '../../components/lw/LwChip.vue';
import LwMenu from '../../components/lw/LwMenu.vue';
import LwDialog from '../../components/lw/LwDialog.vue';
import LwField from '../../components/lw/LwField.vue';
import LwNotice from '../../components/lw/LwNotice.vue';
// Carrier workflows, decision D-08: the availability decision in the pickers.
import AvailabilityNotice from '../../components/scheduling/AvailabilityNotice.vue';
import LwButton from '../../components/lw/LwButton.vue';
import AssigneePicker from './AssigneePicker.vue';
import EquipmentPicker from './EquipmentPicker.vue';
import { assignmentProblem as readAssignmentProblem } from '../Scheduling/schedulingFormat';
import { boardOfferChip, canMessageDrivers, offerNeedsReassign } from '../../lib/driverComms.js';
import {
  sendCarrierConfirmation,
  confirmationFailureText,
  confirmationResult,
} from '../../lib/subhaulerConfirmation.js';

// One page of the board (LwTable's `:page-size` above and the `per_page` the
// fetch sends must be the same number, or the footer's page count describes
// a different pagination than the server is doing).
const PER_PAGE = 25;

export default {
  name: 'LoadList',
  components: {
    LwTable,
    LwChip,
    LwMenu,
    LwDialog,
    LwField,
    LwNotice,
    LwButton,
    AvailabilityNotice,
    AssigneePicker,
    EquipmentPicker,
  },
  // Owns this page's printer, and releases its iframe on destroy. Dispatch
  // renders two of these lists, so this file alone was worth two leaked
  // browsing contexts per visit to the board (ai_docs/perf/
  // web_memory_investigation.md F1). Nothing is constructed until someone
  // actually prints.
  mixins: [printerMixin, loadSearchMixin],
  props: {
    title: {
      type: String,
      required: true,
    },
    param: {
      type: Object,
      required: true,
    },
    events: {
      type: Object,
      required: true,
    },
    emptyTitle: {
      type: String,
      required: true,
    },
    emptyBody: {
      type: String,
      required: true,
    },
    // Keyed by String(load.id). null means the enrichment fetch has not
    // landed, failed, or this list is the delivered table, which
    // /tracking/live never mentions at all (see the `telemetry` column below
    // and Dispatch.vue's own comment on `showTelemetry`).
    custodyByLoadId: {
      type: Object,
      default: null,
    },
    staleAfterMs: {
      type: Number,
      default: null,
    },
    telemetryFailed: {
      type: Boolean,
      default: false,
    },
    showTelemetry: {
      type: Boolean,
      default: false,
    },
  },
  // How many loads match this list (the server's total, not one page), for the page's tab badge. Taken from the
  // fetch the list already makes (no extra request); null after a failed
  // fetch, so the badge disappears rather than showing a stale number.
  emits: ['count'],
  data() {
    return {
      loads: [],
      // F3: `loads` holds ONE page; `total` is the server's count of every
      // row that matches this list's filter, which is what the footer's
      // page count is computed from.
      total: 0,
      page: 1,
      loading: false,
      error: null,
      sort: null,
      trucks: [],
      drivers: [],
      subhaulers: [],
      driverModalLoad: null,
      // The picker's PATCH is in flight.
      driverModalSaving: false,
      truckModalLoad: null,
      truckModalTruckId: null,
      truckModalTrailerId: null,
      // D-08: the availability decision for the current pick, the reason a
      // scheduling.manage viewer may type to assign over a warning, and the
      // server's refusal when a save answered 409.
      truckAvailability: null,
      truckAvailabilityLoading: false,
      // Spec 37: GET /fleet/blocks?kind=truck, { "<truck id>": [reason] },
      // read when the dialog opens so the truck select can say which are
      // blocked (the trailer table reads its own, spec 46).
      unitBlocks: { truck: {} },
      overrideReason: '',
      assignmentProblem: null,
      cancelModalLoad: null,
      // Spec 44: the "Add to a run…" dialog.
      runPickerLoad: null,
      openRuns: [],
      runsLoading: false,
      runsFailed: false,
      runPickBusy: null,
      runPickProblem: null,
      runPickReason: '',
      runAddedId: null,
      // Load ids with a carrier confirmation send in flight: a second pick
      // of the menu item while the first is out sends nothing.
      confirmingIds: [],
    };
  },
  computed: {
    // Flat, dot-free column keys: every cell is rendered through its own
    // `#cell-<key>` slot above, so nothing here needs LwTable's dotted-path
    // resolution. `telemetry` only exists on the in-progress list --
    // /tracking/live only returns entries for undelivered loads, so a
    // telemetry column on the delivered table would be a column of dashes.
    columns() {
      const cols = [
        { key: 'created_at', label: 'Date', sortable: true },
        { key: 'order_number', label: 'Order', sortable: true },
        { key: 'customer', label: 'Customer', sortable: true },
        { key: 'route', label: 'Route', sortable: false },
        { key: 'custody', label: 'Custody', sortable: false },
      ];
      if (this.showTelemetry) {
        cols.push({ key: 'telemetry', label: 'Tracking', sortable: false });
      }
      cols.push(
        { key: 'driver', label: 'Driver', sortable: true },
        { key: 'equipment', label: 'Truck/Trailer', sortable: true },
        // The header carries a real word rather than an empty <th>: a column
        // header with no text is announced as a bare column number, and this
        // is the column holding every destructive action on the row. LwTable
        // has no visually-hidden header mode, and inventing one for a label
        // this short is a worse trade than simply showing it.
        //
        // Width: a raw px floor has no equivalent role in the spacing scale
        // (the same "no token models this" exception LwMenu's own min-width
        // takes), so this reads a space token as a stand-in unit instead.
        { key: 'actions', label: 'Actions', sortable: false, width: 'var(--lw-space-12)' }
      );
      return cols;
    },
    // The Messages rail entry's roles (lib/driverComms.js).
    canMessage() {
      return canMessageDrivers(this.$store);
    },
    // scheduling.manage is admin and dispatcher (decision D-01).
    canManageScheduling() {
      const getters = (this.$store && this.$store.getters) || {};
      if (getters.isAdmin) return true;
      return !!(getters.user && getters.user.role === 'dispatcher');
    },
    schedulingZone() {
      return orgZone(this.$store);
    },
    // Spec 37: a blocked unit stays selectable (an expired registration
    // can be assigned over with a reason, and the server decides either
    // way) but says why in its label: "T-12 · Registration expired
    // 2026-09-30". The AvailabilityNotice explains once it is picked.
    truckOptions() {
      return this.trucks
        .filter(truck => truck.type === 'truck')
        .map(truck => ({ value: truck.id, label: this.unitLabel(truck, 'truck') }));
    },
    // One key for the pair, so opening the dialog on a load that has both
    // asks once, and changing either asks again with both.
    equipmentPick() {
      return `${this.truckModalTruckId || ''}|${this.truckModalTrailerId || ''}`;
    },
  },
  watch: {
    equipmentPick() {
      this.assignmentProblem = null;
      const params = {};
      if (this.truckModalTruckId) params.truck_id = this.truckModalTruckId;
      // Spec 37: the trailer rides along for its readiness blocks.
      if (this.truckModalTrailerId) params.trailer_id = this.truckModalTrailerId;
      if (Object.keys(params).length) this.checkAvailability('truck', params);
      else this.truckAvailability = null;
    },
  },
  created() {
    this.loadData();
    // Populate the two edit dialogs' option lists. Neither .catch leaves an
    // error on screen: an empty Driver/Truck select is a smaller failure
    // than putting the whole board into an error state over a fetch that
    // only feeds a dialog nobody may open this session.
    this.$http
      .get('/trucks/active')
      .then(res => {
        this.trucks = res.data;
      })
      .catch(() => {});
    this.$http
      .get('/drivers')
      .then(res => {
        this.drivers = res.data;
      })
      .catch(() => {});
    this.$http
      .get('/subhaulers')
      .then(res => {
        this.subhaulers = Array.isArray(res.data) ? res.data : [];
      })
      .catch(() => {});
    this.events.on('change', this.loadData);
  },
  beforeUnmount() {
    this.events.off('change', this.loadData);
  },
  methods: {
    // What the public demo switches off (GET /organization's `demo`); every
    // real tenant answers true. A store-less mount (some tests) allows all.
    demoAllows(capability) {
      const getters = (this.$store && this.$store.getters) || {};
      return getters.demoAllows ? getters.demoAllows(capability) : true;
    },
    // The specific defect LwTable's error state exists to end: today this
    // page has no .catch at all (audit_web.md 3.8, "All 15 cTable list pages
    // have no .catch whatsoever"), so a failed fetch renders pixel-identical
    // to an empty board. Retry calls this same method, so a transient
    // failure recovers exactly the way a fresh page load would.
    //
    // SEMANTIC CHANGE (F3): the fetch is paged and sorted on the server.
    // The client used to receive every row and sort/slice locally; now that
    // it holds one page of 25, a local sort could only rearrange the page
    // it happens to have, which looks sorted and is not. So `sort` rides
    // the request (the same keys LwTable emits are the server's whitelist)
    // and the response is the envelope { results, total, page, per_page }.
    loadData() {
      this.loading = true;
      this.error = null;
      const params = {
        ...(this.param || {}),
        ...this.searchParams,
        page: this.page,
        per_page: PER_PAGE,
      };
      // Recorded at the moment the request is committed to, not when it
      // lands, so the empty state names the term the server was asked about
      // rather than whatever has been typed since (loadSearch.js).
      this.markSearchApplied();
      if (this.sort) {
        params.sort = this.sort.key;
        params.dir = this.sort.direction;
      }
      return this.$http
        .get('/loads', { params })
        .then(res => {
          // Rows can leave the list between polls (marked delivered,
          // cancelled): a page that has slid past the end is re-read at the
          // new last page rather than shown as an empty board with a total
          // that says otherwise. One extra request, only in that case.
          const lastPage = Math.max(1, Math.ceil(res.data.total / PER_PAGE));
          if (this.page > lastPage) {
            this.page = lastPage;
            return this.loadData();
          }
          this.loads = res.data.results;
          this.total = res.data.total;
          this.$emit('count', this.total);
          return undefined;
        })
        .catch(() => {
          this.error = 'The board could not reach the server.';
          this.$emit('count', null);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Both of these refetch instead of rearranging: the server owns the
    // order and the slice. A new sort starts from page 1 -- the first page
    // of the new order is the only page that means anything (LwTable's own
    // reset-on-rows watcher held this rule when the pagination was local).
    handleSort(payload) {
      this.sort = payload;
      this.page = 1;
      this.loadData();
    },
    handlePageChange(page) {
      this.page = page;
      this.loadData();
    },
    // The debounce fires here. Page 1 for the same reason a new sort resets
    // it: page 4 of the old answer is not a page of the new one, and a
    // dispatcher who searches and lands on an empty page 4 reads it as the
    // search having found nothing.
    onSearchChanged() {
      this.page = 1;
      this.loadData();
    },
    handleRowActivate(row) {
      this.$router.push({ name: 'LoadsView', params: { id: row.id } });
    },
    openLoadMessages(load) {
      this.$router.push({ name: 'LoadsView', params: { id: load.id }, query: { tab: 'messages' } });
    },
    // The tenant's zone (lib/orgTime.js): the board's created-on column and
    // the sheet the board prints read the same setting, so a load does not
    // change date between the row and the paperwork.
    formatDate(load) {
      return moment(load.created_at).tz(orgZone(this.$store)).format('M/D/Y');
    },
    orderNumber(load) {
      return `${load.id}-${load.edit_number || 0}`;
    },
    customerName(load) {
      return load.customer ? load.customer.name : '';
    },
    customerTitle(load) {
      if (!load.customer) return '';
      const lines = [];
      if (load.customer.address) lines.push(load.customer.address);
      if (load.customer.phone) lines.push(load.customer.phone);
      return lines.join('\n');
    },
    formatRoute(stops) {
      // SEC-N: see the template comment above and escapeHtml.js's header
      // comment. Escape the untrusted stop name FIRST, then add the arrow;
      // never the other order.
      const result = (stops || []).reduce((accum, stop) => {
        accum.push(escapeHtml(stop.location.name));
        accum.push('<i class="fa fa-long-arrow-right"></i>');
        return accum;
      }, []);
      result.pop();
      return result.join(' ');
    },
    equipmentFor(load) {
      // Loads carry both truck.id (surrogate, used to save) and
      // truck.unit_number (what the yard calls it); the table shows
      // unit_number.
      if (!load.truck && !load.trailer) return 'No truck/trailer set';
      if (!load.truck) return `No truck set/${load.trailer.unit_number}`;
      if (!load.trailer) return `${load.truck.unit_number}/no trailer set`;
      return `${load.truck.unit_number}/${load.trailer.unit_number}`;
    },
    entryFor(load) {
      if (!this.custodyByLoadId) return null;
      return this.custodyByLoadId[String(load.id)] || null;
    },
    custodyFor(load) {
      return custodyForLoad(load, this.entryFor(load));
    },
    telemetryFor(load) {
      return telemetryForLoad({
        custody: this.custodyFor(load),
        entry: this.entryFor(load),
        staleAfterMs: this.staleAfterMs,
      });
    },
    // "Not asked" (state.js's default label for telemetry.unknown) is true
    // when nobody has answered yet. It stops being true once the fetch has
    // actually run and failed -- "Tracking unavailable" is LwChip's `label`
    // override prop used for exactly the surface-specific correction it
    // exists for (LwChip.vue's header comment), applied only to the
    // `unknown` value, because that is the only value telemetryForLoad can
    // still produce once custodyByLoadId has been nulled out by a failed
    // fetch (Dispatch.vue's loadTelemetry .catch).
    telemetryLabel(entry) {
      if (this.telemetryFailed && entry.value === 'unknown') return 'Tracking unavailable';
      return null;
    },
    // Spec 39: the offer chip under the driver name, or null.
    offerChipFor(load) {
      return boardOfferChip(load.assignment_offer, load, this.schedulingZone);
    },
    printLoad(load) {
      const content = renderSheet(loadsPrint, { load });
      this.$printer.print(content.el, [content.cssText]);
    },
    // D-08: ask the server's one availability decision for this load and
    // the picked driver or truck. Never an error state: a failed check shows
    // nothing and the save still answers for itself with a 409.
    checkAvailability(kind, params) {
      const load = this.truckModalLoad;
      if (!load) return undefined;
      const query = Object.entries({ load_id: load.id, ...params })
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');
      this[`${kind}AvailabilityLoading`] = true;
      this[`${kind}Availability`] = null;
      return Promise.resolve(this.$http.get(`/scheduling/availability?${query}`))
        .then(res => {
          this[`${kind}Availability`] = res && res.data && res.data.status ? res.data : null;
        })
        .catch(() => {
          this[`${kind}Availability`] = null;
        })
        .finally(() => {
          this[`${kind}AvailabilityLoading`] = false;
        });
    },
    overrideOffered(decision) {
      return !!(this.canManageScheduling && decision && decision.status !== 'available');
    },
    // The PATCH body carries `scheduling_override` only when a reason was
    // typed, so an ordinary assignment is byte-for-byte what it always was.
    withOverride(body) {
      const reason = String(this.overrideReason || '').trim();
      return reason ? { ...body, scheduling_override: { reason } } : body;
    },
    assignmentFailed(err) {
      this.assignmentProblem = readAssignmentProblem(err && err.response);
      this.notify(this.assignmentProblem.title, 'danger');
    },
    // Spec 39: `assignment_response_minutes` only when the picker sent a
    // deadline (acceptance on), so an ordinary assignment's body is unchanged.
    setDriver(loadId, driverId, reason = null, responseMinutes = null) {
      const body = { driver_id: driverId };
      if (reason) body.scheduling_override = { reason };
      if (typeof responseMinutes === 'number') body.assignment_response_minutes = responseMinutes;
      return this.$http.patch(`/loads/${loadId}`, body).then(() => {
        this.events.emit('change');
      });
    },
    // The API clears driver_id when subhauler_id is set (and the reverse),
    // so each assignment sends only its own key.
    setSubhauler(loadId, subhaulerId) {
      return this.$http.patch(`/loads/${loadId}`, { subhauler_id: subhaulerId }).then(() => {
        this.events.emit('change');
      });
    },
    setTruckTrailer(loadId, truckId, trailerId) {
      return this.$http
        .patch(`/loads/${loadId}`, this.withOverride({ truck_id: truckId, trailer_id: trailerId }))
        .then(() => {
          this.events.emit('change');
        });
    },
    deleteLoad(loadId) {
      return this.$http.delete(`/loads/${loadId}`).then(() => {
        this.events.emit('change');
      });
    },
    markDelivered(id) {
      if (!id) throw 'ID missing from markDelivered()';
      return this.$http.patch(`/loads/${id}`, { delivered: 1 }).then(() => {
        this.events.emit('change');
      });
    },
    unmarkDelivered(id) {
      if (!id) throw 'ID missing from unmarkDelivered()';
      return this.$http.patch(`/loads/${id}`, { delivered: 0 }).then(() => {
        this.events.emit('change');
      });
    },
    openDriverModal(load) {
      this.assignmentProblem = null;
      this.driverModalLoad = load;
    },
    closeDriverModal() {
      this.driverModalLoad = null;
      this.driverModalSaving = false;
      this.assignmentProblem = null;
    },
    // The picker's Select (or "Assign with reason"): assign at once. A
    // refusal stays in the dialog with the server's reasons.
    assignFromPicker({ kind, id, reason = null, responseMinutes = null }) {
      if (!this.driverModalLoad || this.driverModalSaving) return undefined;
      const loadId = this.driverModalLoad.id;
      this.assignmentProblem = null;
      this.driverModalSaving = true;
      const request =
        kind === 'subhauler'
          ? this.setSubhauler(loadId, id)
          : this.setDriver(loadId, id, reason, responseMinutes);
      return request
        .then(() => {
          this.closeDriverModal();
        })
        .catch(err => {
          this.driverModalSaving = false;
          this.assignmentFailed(err);
        });
    },
    openTruckModal(load) {
      this.truckModalLoad = load;
      this.truckModalTruckId = load.truck ? load.truck.id : null;
      this.truckModalTrailerId = load.trailer ? load.trailer.id : null;
      this.fetchUnitBlocks(load);
    },
    // Spec 37: which trucks the readiness hook blocks, asked as the dialog
    // opens, for THIS load's commitment interval (`load_id`: the server
    // resolves the interval the same way the assignment check does, so a
    // label and the save agree). A failure labels nothing: the save still
    // answers for itself. Trailers say why in EquipmentPicker (spec 46).
    fetchUnitBlocks(load) {
      const forLoad = load && load.id != null ? `&load_id=${encodeURIComponent(load.id)}` : '';
      return Promise.resolve(this.$http.get(`/fleet/blocks?kind=truck${forLoad}`))
        .then(res => {
          const blocked = res && res.data && res.data.blocked;
          return blocked && typeof blocked === 'object' && !Array.isArray(blocked) ? blocked : {};
        })
        .catch(() => ({}))
        .then(truck => {
          this.unitBlocks = { truck };
        });
    },
    // Spec 46: the trailer table's pick. Ids go up as numbers, as the old
    // select sent them; a reason typed there becomes the dialog's override
    // reason, which Save sends as scheduling_override.
    pickTrailer({ id, reason }) {
      this.truckModalTrailerId = id == null ? null : Number(id);
      if (reason) this.overrideReason = reason;
    },
    unitLabel(unit, kind) {
      const reasons = (this.unitBlocks[kind] || {})[String(unit.id)] || [];
      if (!reasons.length) return unit.unit_number;
      // The hook's message names the unit ("Unit T-12: Registration expired
      // 2026-09-30."); the option already does, so the prefix is dropped.
      const prefix = `Unit ${unit.unit_number}: `;
      let why = String(reasons[0].message || reasons[0].code || 'Blocked');
      if (why.startsWith(prefix)) why = why.slice(prefix.length);
      why = why.replace(/\.$/, '');
      const more = reasons.length > 1 ? ` (+${reasons.length - 1} more)` : '';
      return `${unit.unit_number} · ${why}${more}`;
    },
    closeTruckModal() {
      this.truckModalLoad = null;
      this.truckModalTruckId = null;
      this.truckModalTrailerId = null;
      this.truckAvailability = null;
      this.overrideReason = '';
      this.assignmentProblem = null;
    },
    confirmSetTruckTrailer() {
      if (!this.truckModalLoad) return;
      const loadId = this.truckModalLoad.id;
      this.setTruckTrailer(loadId, this.truckModalTruckId, this.truckModalTrailerId)
        .then(() => {
          this.closeTruckModal();
        })
        .catch(err => this.assignmentFailed(err));
    },
    closeCancelModal() {
      this.cancelModalLoad = null;
    },
    confirmCancelLoad() {
      if (!this.cancelModalLoad) return;
      const loadId = this.cancelModalLoad.id;
      this.deleteLoad(loadId).then(() => {
        this.closeCancelModal();
      });
    },
    // The board has no notice area of its own (a page of 25 rows with two
    // lists on one screen has nowhere a per-list banner would read as
    // belonging to the row), so a row action's result is a toast, the same
    // store module BillingList's invoice Send reports through. Guarded on
    // $store the way BillingList is, for tests that mount without one.
    notify(message, tone) {
      if (this.$store && typeof this.$store.dispatch === 'function') {
        this.$store.dispatch('toasts/notify', { message, tone });
      }
    },
    /**
     * Spec 30 phase B: send this load's carrier confirmation to its
     * subhauler. A 201 can still be a failed send (status 'error', with
     * Ladeway's sentence in status_message), and a refusal's title + detail
     * say what to fix (lib/subhaulerConfirmation.js).
     */
    sendConfirmation(load) {
      if (this.confirmingIds.indexOf(load.id) !== -1) return Promise.resolve();
      this.confirmingIds.push(load.id);
      return sendCarrierConfirmation(this.$http, load.id)
        .then(doc => {
          const result = confirmationResult(doc);
          this.notify(result.message, result.tone);
        })
        .catch(err => {
          this.notify(confirmationFailureText(err), 'danger');
        })
        .finally(() => {
          this.confirmingIds = this.confirmingIds.filter(id => id !== load.id);
        });
    },
    // Spec 44: the "Add to a run…" dialog. Open runs are the ones not yet
    // delivered, less the run this load already rides (if the row says).
    openRunPicker(load) {
      this.runPickerLoad = load;
      this.openRuns = [];
      this.runsFailed = false;
      this.runPickProblem = null;
      this.runPickReason = '';
      this.runAddedId = null;
      this.runsLoading = true;
      return Promise.resolve(this.$http.get('/runs'))
        .then(res => {
          const runs = (res && res.data && res.data.runs) || [];
          this.openRuns = runs.filter(
            run => !run.delivered && String(run.id) !== String(load.run_id)
          );
        })
        .catch(() => {
          this.runsFailed = true;
        })
        .finally(() => {
          this.runsLoading = false;
        });
    },
    closeRunPicker() {
      this.runPickerLoad = null;
      this.runPickBusy = null;
      this.runPickProblem = null;
      this.runPickReason = '';
      this.runAddedId = null;
    },
    runOptionLabel(run) {
      const driver = run.driver && run.driver.name ? run.driver.name : 'No driver';
      const truck = run.truck ? run.truck.unit_number : 'No truck';
      const count = run.load_count != null ? run.load_count : (run.loads || []).length;
      return `${driver} · ${truck} · ${count} ${count === 1 ? 'load' : 'loads'}`;
    },
    addToRun(run) {
      const load = this.runPickerLoad;
      if (!load || this.runPickBusy !== null) return undefined;
      this.runPickBusy = run.id;
      this.runPickProblem = null;
      const body = { load_id: load.id };
      const reason = String(this.runPickReason || '').trim();
      if (reason) body.scheduling_override = { reason };
      return this.$http
        .post(`/runs/${run.id}/loads`, body)
        .then(() => {
          this.runPickBusy = null;
          this.runAddedId = run.id;
          this.runPickReason = '';
          this.notify(`Load ${this.orderNumber(load)} added to run ${run.id}.`, 'success');
          this.events.emit('change');
        })
        .catch(err => {
          this.runPickBusy = null;
          this.runPickProblem = readAssignmentProblem(err && err.response);
        });
    },
    runHref(id) {
      if (this.$router && typeof this.$router.resolve === 'function') {
        return this.$router.resolve({ name: 'RunView', params: { id: String(id) } }).href;
      }
      return `/dispatch/runs/${id}`;
    },
    // A plain left click takes the SPA path; a modified click opens a tab.
    openRun(event) {
      const id = this.runAddedId;
      if (
        id === null ||
        !event ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0 ||
        event.defaultPrevented
      ) {
        return;
      }
      event.preventDefault();
      this.closeRunPicker();
      this.$router.push({ name: 'RunView', params: { id: String(id) } });
    },
    // Preserves today's row-action list and visibility rules exactly
    // (audit_web.md 3.4: every one of these used to be an unreachable
    // `<div class="c-menu-action" @click>`; LwMenu's items are real
    // `<button role="menuitem">`s instead).
    actionsFor(load) {
      const items = [
        {
          label: 'View details',
          iconBefore: 'fa-file-text-o',
          onSelect: () => this.handleRowActivate(load),
        },
        {
          label: 'Print dispatch sheet',
          iconBefore: 'fa-print',
          onSelect: () => this.printLoad(load),
        },
      ];
      if (!load.delivered) {
        items.push({
          label: 'Mark delivered',
          iconBefore: 'fa-check',
          onSelect: () => this.markDelivered(load.id),
        });
      } else {
        items.push({
          label: 'Unmark delivered',
          iconBefore: 'fa-times-circle',
          onSelect: () => this.unmarkDelivered(load.id),
        });
      }
      // The load's thread with its driver, one click from the board (Mark,
      // 2026-10-14: dispatch had no easy way to message a driver). It opens
      // the load page's Messages tab rather than a composer here, so the
      // dispatcher sees what was already said before adding to it. Only with
      // a driver of ours: a subhauler's driver has no app login to reach,
      // and an unassigned load's thread would reach nobody. Delivered loads
      // too, since "where is the signed POD" comes after delivery.
      if (load.driver && !load.subhauler && this.canMessage) {
        items.push({
          label: 'Message driver',
          iconBefore: 'fa-comments',
          onSelect: () => this.openLoadMessages(load),
        });
      }
      // Spec 39: a declined or unanswered offer's one-click way back to the
      // picker, ahead of the general "Set driver" entry it duplicates.
      if (!load.delivered && offerNeedsReassign(load.assignment_offer)) {
        items.push({
          label: 'Reassign driver',
          iconBefore: 'fa-refresh',
          onSelect: () => this.openDriverModal(load),
        });
      }
      if (!load.delivered) {
        items.push(
          {
            label: 'Set driver or subhauler',
            iconBefore: 'fa-user',
            onSelect: () => this.openDriverModal(load),
          },
          {
            label: 'Set truck',
            iconBefore: 'fa-truck',
            onSelect: () => this.openTruckModal(load),
          }
        );
      }
      // Spec 44: put this load on an existing run (several loads, one
      // truck). Not for a subhauler's load: it has no truck of ours.
      if (!load.delivered && !load.subhauler) {
        items.push({
          label: 'Add to a run…',
          iconBefore: 'fa-list-ol',
          onSelect: () => this.openRunPicker(load),
        });
      }
      // Spec 30 phase B: only a load handed to a subhauler has anyone to
      // confirm it to. Offered on delivered loads too: a subhauler who never
      // signed is still owed the paperwork.
      if (load.subhauler) {
        items.push({
          label: 'Send carrier confirmation',
          iconBefore: 'fa-paper-plane',
          // Off on the public demo (spec 33 8.2); the server refuses it too.
          disabled: !this.demoAllows('subhauler_confirmation_send'),
          onSelect: () => this.sendConfirmation(load),
        });
      }
      items.push({
        label: 'Cancel load',
        iconBefore: 'fa-trash',
        destructive: true,
        onSelect: () => {
          this.cancelModalLoad = load;
        },
      });
      return items;
    },
  },
};
</script>

<style scoped lang="scss">
.lw-p-board {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-3);
}

.lw-p-board__title {
  margin: 0;
  font: var(--lw-type-subhead);
  color: var(--lw-fg-primary);
}

// Same toolbar shape CustomersList uses, so the one find box in the product
// does not have two layouts. The bottom margin is what separates it from the
// table; the section's own gap does not apply here because the heading, the
// toolbar and the table are siblings in a block flow, not a flex column.
.lw-p-board__toolbar {
  display: flex;
  justify-content: flex-start;
  margin: var(--lw-space-3) 0;
}

.lw-p-board__search {
  // The rail width is the narrowest a search box stays useful at; letting it
  // fill the row would make one input read as a page section.
  width: var(--lw-layout-rail-width);
}

.lw-p-board__telemetry {
  display: inline-flex;
  align-items: center;
  gap: var(--lw-space-1);
}

.lw-p-board__offer {
  display: block;
  margin-top: var(--lw-space-1);
}

.lw-p-board__subhauler {
  display: block;
}

.lw-p-board__subhauler-driver {
  display: block;
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
}

.lw-p-board__telemetry-age {
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
}

.lw-p-board__telemetry-empty {
  color: var(--lw-fg-tertiary);
}

// Standard visually-hidden clip, matching LwTable/LwButton's own copy of the
// same technique: off-flow, zero-area, still read by a screen reader.
.lw-p-board__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* D-08: the server's reasons for a refused assignment, inside the dialog. */
.lw-p-load-list__refusal {
  margin: 0;
  padding-left: var(--lw-space-4);
}

// Spec 44: the "Add to a run…" dialog.
.lw-p-load-list__runs {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--lw-space-2);
}

.lw-p-load-list__runs-status {
  margin: 0;
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-secondary);
}
</style>
